import output from "_src/engine/output.js";
import Drawable from "_src/engine/drawable";
import primitiveFactory from "_src/engine/factories/primitive";
import objFactory from "_src/engine/factories/object";
import bufferFactory from "_src/engine/factories/pixel_buffer";
import minmaxUtils from "_src/engine/utility/minmax";
import {
  geometry,
  primitiveType,
  pixelBufferType,
  blendFactor,
  textureMinFilter,
  textureMagFilter,
  drawMode,
  stencilAction,
  stencilFunc
} from "_src/engine/constants";

import DefaultShader from "_src/shaders/default/default_shader";
import LineShader from "_src/shaders/line/line";
import loader from "_src/loader";
import { screenUtils } from "_src/client/utils";

import { vec3, quat } from "_src/libs/gl-matrix";
import appState from "_src/app_state";

import ssMovementPng from "_assets/textures/ss_movement.png";
import ssMovementSelectedPng from "_assets/textures/ss_movement_selected.png";
import pointPng from "_assets/textures/point.png";
import ringPng from "_assets/textures/ring.png";
import ringSelectedPng from "_assets/textures/ring_selected.png";
import cursorArrowPng from "_assets/textures/cursor_arrow.png";
import cursorDotPng from "_assets/textures/cursor_dot.png";
import linePng from "_assets/textures/line.png";

import gizmoObj from "_assets/meshes/gizmo.obj";
import {colorType, lineColorType} from "_src/constants";

const USUAL_RED   = [1.0, 0.439, 0.541];
const USUAL_GREEN = [0.227, 0.890, 0.239];
const USUAL_BLUE  = [0.188, 0.219, 0.886];

export const selectedColors = {
  red   : USUAL_RED,
  green : USUAL_GREEN,
  blue  : USUAL_BLUE
};

export const hiddenColors = {
  red   : [0.968, 0.686, 0.741],
  green : [0.580, 0.917, 0.592],
  blue  : [0.556, 0.576, 0.917]
};

export const gizmoHelpers = {
  ROTATION_CURVE     : 0,
  LOCATION_ARROWS    : 1,
  LOCATION_AXES      : 2,
  LOCATION_PLANES    : 3,
  SCALE_PLANES       : 4,
  ROTATION_AXES      : 5,
  ROTATION_INDICATOR : 6,
  LOCATION_DRAG      : 7
};

export class Gizmo {

  constructor() {

    this.origin = null;
    this.cursor = null;
    this.widget = null;
    this.cursorArrow = null;

    const tmpShader = new DefaultShader({});

    this.locOrigin = new Drawable(tmpShader);
    this.rotOrigin = new Drawable(tmpShader);
    this.scaOrigin = new Drawable(tmpShader);

    this.locOrigin.add(this.scaOrigin);
    this.scaOrigin.add(this.rotOrigin);

    this.helpers = {};

  }

  addToScene(scene) {
    scene.add(this.locOrigin);
  }

  addHelper(helperID, helper) {
    this.helpers[helperID] = helper;
  }

  setTranslation(x, y, z) {
    this.locOrigin.setTranslation(x, y, z);
  }

  getTranslation() {
    return this.locOrigin.trans;
  }

  setRotation(x, y, z, w) {
    return this.rotOrigin.setRotation(x, y, z, w);
  }

  getRotation() {
    return this.rotOrigin.quat;
  }

  setScale(x, y, z) {
    this.scaOrigin.setScale(x, y, z);
    this.cursorArrow.setScale(x, y, z);
  }

  setOrigin(object3D) {

    this.origin = object3D;

    this.getScaleConstraint().add(this.origin);

  }

  setWidget(object3D) {

    if (!this.origin) {
      return false;
    }

    this.widget = object3D;
    this.origin.add(object3D);

  }

  getOrigin() {
    return this.origin;
  }

  getHelper(helperID) {
    return this.helpers[helperID];
  }

  getLocationConstraint() {
    return this.locOrigin;
  }

  getScaleConstraint() {
    return this.scaOrigin;
  }

  getRotationConstraint() {
    return this.rotOrigin;
  }

  updateBoundings() {

    this.locOrigin.updateMatrix();

    const arrowHelper = this.helpers[gizmoHelpers.LOCATION_ARROWS];
    const scaleHelper = this.helpers[gizmoHelpers.SCALE_PLANES];
    const rotationHelper = this.helpers[gizmoHelpers.ROTATION_AXES];

    const gizmoMinxam = this.locOrigin.minmaxWorld;
    minmaxUtils.reset(gizmoMinxam);

    arrowHelper.updateBoundings(gizmoMinxam);
    scaleHelper.updateBoundings(gizmoMinxam);
    rotationHelper.updateBoundings(gizmoMinxam);

  }

  cleanup() {

    for (let helperid in this.helpers) {
      this.helpers[helperid].cleanup();
    }

    if (this.origin) this.origin.cleanup();
    if (this.cursor) this.cursor.cleanup();
    if (this.widget) this.widget.cleanup();

    this.origin = null;
    this.cursor = null;
    this.widget = null;

    const tmpShader = new DefaultShader({});

    this.locOrigin = new Drawable(tmpShader);
    this.rotOrigin = new Drawable(tmpShader);
    this.scaOrigin = new Drawable(tmpShader);

    this.locOrigin.add(this.scaOrigin);
    this.scaOrigin.add(this.rotOrigin);

    this.helpers = {};

  }

}

export function setupGizmo(gizmo) {

  return setupGizmoComponents(gizmo).then((loaded) => {

    if (!loaded) {
      return false;
    }

    setupInitialRotation(gizmo);
    setupInitialColor(gizmo);
    setupInitinalLocation(gizmo);
    setupInitialVisibility(gizmo);

    setupAdditionalComponents(gizmo);

    return true;

  });

}

function setupInitialRotation(gizmo) {

  const quatPX = quat.setAxisAngle(quat.create(), geometry.AXIS_X, Math.PI / 2);
  const quatPZ = quat.setAxisAngle(quat.create(), geometry.AXIS_Z, Math.PI / 2);
  const quatMX = quat.setAxisAngle(quat.create(), geometry.AXIS_X, -Math.PI / 2);
  const quatMZ = quat.setAxisAngle(quat.create(), geometry.AXIS_Z, -Math.PI / 2);
  const quatPY = quat.setAxisAngle(quat.create(), geometry.AXIS_Y, Math.PI / 2);

  gizmo.getHelper(gizmoHelpers.ROTATION_CURVE).setComponentsOrientation(quatPZ, null, quatMX);
  gizmo.getHelper(gizmoHelpers.LOCATION_PLANES).setComponentsOrientation(quatPZ, null, quatMX);
  gizmo.getHelper(gizmoHelpers.LOCATION_ARROWS).setComponentsOrientation(quatMZ, null, quatPX);
  gizmo.getHelper(gizmoHelpers.LOCATION_AXES).setComponentsOrientation(quatMZ, null, quatPX);
  gizmo.getHelper(gizmoHelpers.ROTATION_AXES).setComponentsOrientation(quatPZ, null, quatMX);
  gizmo.getHelper(gizmoHelpers.ROTATION_INDICATOR).setComponentsOrientation(quatPY, null, quatMX);

}

function setupInitialColor(gizmo) {

  gizmo.getHelper(gizmoHelpers.ROTATION_CURVE).setComponentsColor(USUAL_RED, USUAL_GREEN, USUAL_BLUE);
  gizmo.getHelper(gizmoHelpers.LOCATION_PLANES).setComponentsColor(USUAL_RED, USUAL_GREEN, USUAL_BLUE);
  gizmo.getHelper(gizmoHelpers.LOCATION_ARROWS).setComponentsColor(USUAL_RED, USUAL_GREEN, USUAL_BLUE);
  gizmo.getHelper(gizmoHelpers.LOCATION_AXES).setComponentsColor(USUAL_RED, USUAL_GREEN, USUAL_BLUE);
  gizmo.getHelper(gizmoHelpers.SCALE_PLANES).setComponentsColor(USUAL_RED, USUAL_GREEN, USUAL_BLUE);
  gizmo.getHelper(gizmoHelpers.ROTATION_AXES).setComponentsColor(USUAL_RED, USUAL_GREEN, USUAL_BLUE);
  gizmo.getHelper(gizmoHelpers.ROTATION_INDICATOR).setComponentsColor(USUAL_RED, USUAL_GREEN, USUAL_BLUE);

}

function setupInitinalLocation(gizmo) {

  const positionX = vec3.fromValues(1.3, 0, 0);
  const positionY = vec3.fromValues(0, 1.3, 0);
  const positionZ = vec3.fromValues(0, 0, 1.3);

  gizmo.getHelper(gizmoHelpers.SCALE_PLANES).setLocationOffset(positionX, positionY, positionZ);

  vec3.set(positionX, 0, 0.2, 0.2);
  vec3.set(positionY, 0.2, 0, 0.2);
  vec3.set(positionZ, 0.2, 0.2, 0);

  gizmo.getHelper(gizmoHelpers.LOCATION_PLANES).setLocationOffset(positionX, positionY, positionZ);

}

function setupInitialVisibility(gizmo) {

  gizmo.getHelper(gizmoHelpers.ROTATION_AXES).hide();

}

function setupAdditionalComponents(gizmo) {

  const helper = gizmo.getHelper(gizmoHelpers.ROTATION_INDICATOR);

  const geometry = primitiveFactory.create(primitiveType.PLANE, { width : 2, height : 2 });
  const blendShader = new DefaultShader({
    transparent: true,
    backFaceCulling: false,
    lighting: false,
    drawDepth: false,
    writeObjectID: false,
    backFaceMode: appState.BM_FRONTFACE,
  });

  blendShader.enableStencilTest();
  blendShader.setStencilFunction(stencilFunc.NOTEQUAL);
  blendShader.setStencilReferenceValue(127);

  for (let component in helper.components) {

    const maskObject = helper.components[component];
    const colorObject = objFactory.createDrawable(blendShader);
    colorObject.setOpacity(0.5);
    colorObject.addGeometryChunk(geometry);

    const dc = maskObject.properties.diffuseColor;
    colorObject.setDiffuseColor(dc[0], dc[1], dc[2]);

    maskObject.add(colorObject);
    colorObject.hide();

  }

}

class TransformHelper {

  constructor(primitive, shader) {

    this.components = {};
    this.pickers = null;

    this.components["X"] = objFactory.createDrawable(shader);
    this.components["Y"] = objFactory.createDrawable(shader);
    this.components["Z"] = objFactory.createDrawable(shader);

    this.components["X"].addGeometryChunk(primitive);
    this.components["Y"].addGeometryChunk(primitive);
    this.components["Z"].addGeometryChunk(primitive);

    this.components["X"].setFrustrumCulling(false);
    this.components["Y"].setFrustrumCulling(false);
    this.components["Z"].setFrustrumCulling(false);

  }

  addPickerGeometry(primitive, shader) {

    this.pickers = {};

    this.pickers["X"] = objFactory.createDrawable(shader);
    this.pickers["Y"] = objFactory.createDrawable(shader);
    this.pickers["Z"] = objFactory.createDrawable(shader);

    this.pickers["X"].addGeometryChunk(primitive);
    this.pickers["Y"].addGeometryChunk(primitive);
    this.pickers["Z"].addGeometryChunk(primitive);

    this.components["X"].add(this.pickers["X"]);
    this.components["Y"].add(this.pickers["Y"]);
    this.components["Z"].add(this.pickers["Z"]);

    this.pickers["X"].hide();
    this.pickers["Y"].hide();
    this.pickers["Z"].hide();

  }

  setDrawMode(mode) {

    for (let component in this.components) {
      for (let chunk of this.components[component].chunks) {
        chunk.setDrawMode(mode)
      }
    }

  }

  updateSphereBillboard(quatX, quatY, quatZ) {

    this.components["X"].copyRotation(quatX);
    this.components["Y"].copyRotation(quatY);
    this.components["Z"].copyRotation(quatZ);

  }

  setComponentsOrientation(quatX, quatY, quatZ) {

    if (quatX) this.components["X"].copyRotation(quatX);
    if (quatY) this.components["Y"].copyRotation(quatY);
    if (quatZ) this.components["Z"].copyRotation(quatZ);

  }

  setComponentsColor(colorX, colorY, colorZ) {

    if (colorX) this.components["X"].setDiffuseColor(colorX[0], colorX[1], colorX[2]);
    if (colorY) this.components["Y"].setDiffuseColor(colorY[0], colorY[1], colorY[2]);
    if (colorZ) this.components["Z"].setDiffuseColor(colorZ[0], colorZ[1], colorZ[2]);

  }

  setComponentsTexture(textureX, textureY, textureZ, slot = 0) {

    if (textureX) useTetxure(this.components["X"], textureX, slot);
    if (textureY) useTetxure(this.components["Y"], textureY, slot);
    if (textureZ) useTetxure(this.components["Z"], textureZ, slot);

  }

  setComponentsScale(scaleX, scaleY, scaleZ) {

    this.components["X"].setScale(scaleX, scaleX, scaleX);
    this.components["Y"].setScale(scaleY, scaleY, scaleY);
    this.components["Z"].setScale(scaleZ, scaleZ, scaleZ);

  }

  setLocationOffset(positionX, positionY, positionZ) {

    this.components["X"].copyTranslation(positionX);
    this.components["Y"].copyTranslation(positionY);
    this.components["Z"].copyTranslation(positionZ);

  }

  setOpacity(opacity) {

    this.components["X"].setOpacity(opacity);
    this.components["Y"].setOpacity(opacity);
    this.components["Z"].setOpacity(opacity);

  }

  castRay(component, from, to, dest) {

    if (!this.pickers) {
      return false;
    }

    return this.pickers[component].castRay(from, to, dest);

  }

  drawOneComponent(component) {

    if (component != "X") this.components["X"].hide();
    if (component != "Y") this.components["Y"].hide();
    if (component != "Z") this.components["Z"].hide();

  }

  updateBoundings(parentMinmax) {
    this.components["X"].updateBounding();
    this.components["Y"].updateBounding();
    this.components["Z"].updateBounding();

    minmaxUtils.expandMinmax(parentMinmax, this.components["X"].minmaxWorld);
    minmaxUtils.expandMinmax(parentMinmax, this.components["Y"].minmaxWorld);
    minmaxUtils.expandMinmax(parentMinmax, this.components["Z"].minmaxWorld);
  }

  hide() {

    this.components["X"].hide();
    this.components["Y"].hide();
    this.components["Z"].hide();

  }

  show() {

    this.components["X"].show();
    this.components["Y"].show();
    this.components["Z"].show();

  }

  hideComponent(component) {
    this.components[component].hide();
  }

  showComponent(component) {
    this.components[component].show();
  }

  addTo(object) {

    object.add(this.components["X"]);
    object.add(this.components["Y"]);
    object.add(this.components["Z"]);

  }

  removeFrom(object) {

    object.remove(this.components["X"]);
    object.remove(this.components["Y"]);
    object.remove(this.components["Z"]);

  }

  cleanup() {

    for (let component in this.components) {
      for (let chunk of this.components[component].chunks) {
        chunk.cleanup();
      }
      for (let texture of this.components[component].textures) {
        texture.cleanup();
      }
    }

  }

}

function loadTexture(path, filtering = true) {

  return loader.load(path, loader.IMAGE).then((image) => {

    if (!image) {
      output.error(output.DEVELOPERS_ERROR, "Couldn't process the gizmo texture.");
      return;
    }

    let minFilter = textureMinFilter.NEAREST;
    let magFilter = textureMagFilter.NEAREST;

    if (filtering) {
      minFilter = textureMinFilter.LINEAR;
      magFilter = textureMagFilter.LINEAR;
    }

    const texture = bufferFactory.create(pixelBufferType.T_RGBA, {width: image.width, height: image.height,
        minFilter, magFilter, immutable: true });
    texture.initMipMapLevelImage(0, image);

    return texture;

  });

}

function createTexturedPlane(size, texture, depthTest = true) {

  const geometry = primitiveFactory.create(primitiveType.PLANE, {width : size, height : size});
  const shader = new DefaultShader({ transparent : true, lighting : false, depthTest });
  const object = objFactory.createDrawable(shader);
  useTetxure(object, texture, 0);
  object.addGeometryChunk(geometry);

  return object;

}

function useTetxure(object, texture, slot) {

  object.setTexture(texture, slot);

  for (let chunk of object.chunks) {
    chunk.markAttributeDirty("a_uv");
  }

  object.shader.setDirective("COLOR_TYPE", colorType.TEXTURE_UV);

}

function addHelperToGizmo(geometry, shader, helperID, gizmo, picker = null, pickerShader = null, opacity = 1.0) {

  const helper = new TransformHelper(geometry, shader);

  if (picker && pickerShader) {
    helper.addPickerGeometry(picker, pickerShader);
  }

  helper.setOpacity(opacity);
  helper.addTo(gizmo.getRotationConstraint());
  gizmo.addHelper(helperID, helper);

}

function setupGizmoComponents(gizmo) {

  const promises = [];

  promises.push(loadTexture(ssMovementPng));
  promises.push(loadTexture(ssMovementSelectedPng));
  promises.push(loadTexture(pointPng));
  promises.push(loadTexture(ringPng));
  promises.push(loadTexture(ringSelectedPng));
  promises.push(loadTexture(cursorArrowPng));
  promises.push(loadTexture(cursorDotPng));
  promises.push(loadTexture(linePng, true));

  promises.push(loader.load(gizmoObj, loader.EXTERNAL).then(objectsData => {

    if (!objectsData || !objectsData.length) {
      output.error(output.DEVELOPERS_ERROR, "Couldn't parse the gizmo geometry.");
      return gizmo;
    }

    const pixelRatio = screenUtils.getPixelRatio();
    const opaqueShader = new DefaultShader({
      backFaceCulling: false,
      lighting: false,
      drawDepth: false,
      writeObjectID: false,
      backFaceMode: appState.BM_FRONTFACE,
    });

    const indicatorShader = new DefaultShader({
      backFaceCulling: false,
      drawColor: false,
      depthTest: false,
      drawDepth: false,
      writeObjectID: false,
    });

    const blendTexuredShader = new DefaultShader({ transparent : true, lighting : false });
    const lineShader = new LineShader({ lineWidth : 1 * pixelRatio });
    const texturedLineShader = new LineShader({ lineWidth : 2 * pixelRatio, transparent : true, colorType : lineColorType.TEXTURE });

    texturedLineShader.setTexturePeriod(0.05);
    texturedLineShader.setSourceBlendFactor(blendFactor.ONE);
    indicatorShader.enableStencilTest();
    indicatorShader.setStencilFrontZPassAction(stencilAction.INCR);
    indicatorShader.setStencilBackZPassAction(stencilAction.DECR);

    const linePrimitive = primitiveFactory.create(primitiveType.LINE, {y1 : 1.05});
    const screenQuadPromotive = primitiveFactory.create(primitiveType.SCREEN_QUAD);

    addHelperToGizmo(objectsData[6].geometry, blendTexuredShader, gizmoHelpers.SCALE_PLANES,
        gizmo, objectsData[8].geometry, opaqueShader);
    addHelperToGizmo(objectsData[7].geometry, opaqueShader, gizmoHelpers.ROTATION_CURVE,
        gizmo, objectsData[3].geometry, opaqueShader);
    addHelperToGizmo(linePrimitive, lineShader, gizmoHelpers.LOCATION_AXES,
        gizmo, objectsData[1].geometry, opaqueShader);
    addHelperToGizmo(objectsData[2].geometry, opaqueShader, gizmoHelpers.ROTATION_AXES,
        gizmo, null, null);
    addHelperToGizmo(objectsData[9].geometry, opaqueShader, gizmoHelpers.LOCATION_PLANES,
        gizmo, objectsData[9].geometry, opaqueShader);
    addHelperToGizmo(objectsData[5].geometry, opaqueShader, gizmoHelpers.LOCATION_ARROWS,
        gizmo, objectsData[0].geometry, opaqueShader);
    addHelperToGizmo(screenQuadPromotive, indicatorShader, gizmoHelpers.ROTATION_INDICATOR,
        gizmo, null, null);

    gizmo.getHelper(gizmoHelpers.ROTATION_INDICATOR).setDrawMode(drawMode.TRIANGLE_FAN);
    gizmo.getHelper(gizmoHelpers.ROTATION_INDICATOR).cleanup();

    const mousePointer = objFactory.createDrawable(texturedLineShader);
    mousePointer.addGeometryChunk(primitiveFactory.create(primitiveType.LINE, {y1 : 1.0}));
    mousePointer.setDiffuseColor(0, 0, 0);
    mousePointer.hide();
    gizmo.getLocationConstraint().add(mousePointer);
    gizmo.cursor = mousePointer;

    return gizmo;

  }));

  return Promise.all(promises).then((textures) => {

    const usualWidgetText = textures[0];
    const selectedWidtedText = textures[1];

    const gizmoWidget = createTexturedPlane(0.2, usualWidgetText, false);
    gizmoWidget.setTexture(usualWidgetText, 1);
    gizmoWidget.setTexture(selectedWidtedText, 2);

    const originTexture = textures[2];

    const gizmoOrigin = createTexturedPlane(0.04, originTexture, false);
    gizmo.setOrigin(gizmoOrigin);
    gizmo.setWidget(gizmoWidget);

    const usualScalePlaneText = textures[3];
    const selectedScalePlaneText = textures[4];

    gizmo.getHelper(gizmoHelpers.SCALE_PLANES).setComponentsTexture(usualScalePlaneText,
        usualScalePlaneText, usualScalePlaneText, 0);
    gizmo.getHelper(gizmoHelpers.SCALE_PLANES).setComponentsTexture(usualScalePlaneText,
        usualScalePlaneText, usualScalePlaneText, 1);
    gizmo.getHelper(gizmoHelpers.SCALE_PLANES).setComponentsTexture(selectedScalePlaneText,
        selectedScalePlaneText, selectedScalePlaneText, 2);

    const arrowCursorText = textures[5];
    const dotCursorText = textures[6];
    const cursorArrow = createTexturedPlane(0.05, arrowCursorText);
    gizmo.getLocationConstraint().add(cursorArrow);
    cursorArrow.setTexture(arrowCursorText, 1);
    cursorArrow.setTexture(dotCursorText, 2);
    gizmo.cursorArrow = cursorArrow;
    cursorArrow.hide();

    const lineText = textures[7];
    gizmo.cursor.setTexture(lineText, 0);

    return true;

  }).catch((error) => {
    if (!loader.isLegalError(error)) {
      output.error(output.DEVELOPERS_ERROR, "Couldn't load gizmo data. " + error);
    }
    return false;
  });

}
